import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Users, CheckCircle2, TrendingUp, FileEdit, ThumbsUp, Flag } from "lucide-react";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</span>
      <Icon className="h-4 w-4 text-slate-400" />
    </div>
    <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="mt-1 text-sm text-slate-500">{sub}</div>}
  </div>
);

const Bars = ({ data, title }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-medium text-slate-700">{title}</h3>
      <div className="mt-4 space-y-3">
        {entries.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
        {entries.map(([k, v]) => (
          <div key={k}>
            <div className="mb-1 flex justify-between text-sm"><span className="text-slate-600">{k}</span><span className="font-medium text-slate-900">{v}</span></div>
            <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-green-700" style={{ width: `${(v / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/summary").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="min-h-screen bg-slate-50"><Nav /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Nav />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">Hypothesis validation</div>
        <h1 className="font-serif text-4xl font-semibold tracking-tight text-slate-900">Product insights</h1>
        <p className="mt-3 max-w-2xl text-slate-600">Privacy-conscious usage analytics testing whether combining intake, issue-spotting, document review, research and form preparation saves consumers time.</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stat testid="stat-matters" icon={Users} label="Matters started" value={d.total_matters} />
          <Stat testid="stat-completed" icon={CheckCircle2} label="Intakes completed" value={d.completed_intakes} sub={`${d.abandonment_rate}% abandoned`} />
          <Stat testid="stat-drafts" icon={FileEdit} label="Drafts created" value={d.drafts_created} sub={`${d.drafts_edited} edited by consumers`} />
          <Stat testid="stat-helpful" icon={ThumbsUp} label="Found helpful" value={`${d.feedback_helpful}/${d.feedback_total}`} sub="feedback responses" />
          <Stat testid="stat-reported" icon={Flag} label="Outputs reported" value={d.reported_outputs} sub="inaccurate / unclear" />
          <Stat testid="stat-events" icon={TrendingUp} label="Total AI actions" value={(d.event_counts?.chronology_generated || 0) + (d.event_counts?.issues_generated || 0) + (d.event_counts?.research_run || 0) + (d.event_counts?.documents_compared || 0)} />
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Bars title="Which problems users select" data={d.problem_distribution} />
          <Bars title="Documents uploaded by type" data={d.document_types} />
          <Bars title="Feature usage (events)" data={d.event_counts} />
        </div>
      </main>
    </div>
  );
}
